import { useState } from 'react';
import {
	PolicyTransaction,
	PolicyTransactionSchema,
	update,
} from '@/api/northlight/policy-transaction';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdatePolicyTransaction({
	policyTransaction,
}: {
	policyTransaction: PolicyTransaction;
}) {
	const statuses: Array<string> = [
		'Bound',
		'BOR Transfer',
		'Cancellation',
		'Endorsement',
		'Error',
		'Submission',
	];

	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationFn: (updatedPolicyTransaction: PolicyTransaction) =>
			update(updatedPolicyTransaction),
		onSuccess: () => {
			toast.success(`Success! Transaction has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);
			queryClient.invalidateQueries({
				queryKey: ['policies-' + policyTransaction.fileIngestionId],
			});
		},
		onError: () => {
			toast.error(`Error! Transaction could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const form = useForm<PolicyTransaction>({
		resolver: zodResolver(PolicyTransactionSchema),
		defaultValues: policyTransaction,
		mode: 'all',
	});

	const onSubmit: SubmitHandler<PolicyTransaction> = (
		values: PolicyTransaction,
	) => mutate(values);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update policy transaction</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Sheet open={open} onOpenChange={(open) => setOpen(open)}>
				<SheetContent>
					<SheetHeader>
						<SheetTitle>Update Policy Transaction</SheetTitle>
						<SheetDescription></SheetDescription>
					</SheetHeader>
					<Form {...form} >
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="space-y-4">
								<FormField
									control={form.control}
									name="status"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Status</FormLabel>
											<FormControl>
												<Select
													{...field}
													value={field.value}
													onValueChange={field.onChange}
												>
													<SelectTrigger>
														<SelectValue placeholder="Select a status..." />
													</SelectTrigger>
													<SelectContent>
														<SelectGroup>
															<SelectLabel>Statuses</SelectLabel>
															{statuses!.map((status) => (
																<SelectItem value={status} key={status}>{status}</SelectItem>
															))}
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="policyNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Policy #</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="riskState"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Risk State</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="policyId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Sub #</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="transSummaryId"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Invoice #</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="premium"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Premium</FormLabel>
											<FormControl>
												<Input {...field} value={field.value ?? ''} disabled={true} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="notes"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Notes</FormLabel>
											<FormControl>
												<Textarea
													{...field}
													value={field.value ?? ''}
													disabled={true}
													className="resize-h"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="userNotes"
									render={({ field }) => (
										<FormItem>
											<FormLabel>User Notes</FormLabel>
											<FormControl>
												<Textarea
													{...field}
													placeholder="User Notes..."
													value={field.value ?? ''}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="mt-9 flex justify-end">
								<Button
									type="submit"
									disabled={!form.formState.isValid || form.formState.isSubmitting}
								>
									{form.formState.isSubmitting ? 'Updating...' : 'Update'}
								</Button>
							</div>
						</form>
					</Form>
				</SheetContent>
			</Sheet>
		</div>
	);
}
